import { Link, useLocation } from 'react-router-dom';
import {
    Bell as BellIcon,
    Search as SearchIcon,
    Shield as ShieldIcon,
    Activity as ActivityIcon,
    Hotel as HotelIcon,
    Image as ImageIcon,
    Gamepad2 as GamepadIcon,
    LogOut as LogOutIcon,
    Bot,
    Heart as HeartIcon
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
    Avatar,
    Button,
} from '@randompedestrian07/ui';
import { clsx } from 'clsx';

export const Sidebar = () => {
    const { user, logout } = useAuth();
    const location = useLocation();

    const isActive = (path: string) => {
        if (path === '/' && location.pathname === '/') return true;
        if (path !== '/' && location.pathname.startsWith(path)) return true;
        return false;
    };

    const navItems = [
        { name: 'System Health', path: '/', icon: <ActivityIcon size={18} /> },
        { name: 'Hotel Bull', path: '/hotelbull', icon: <HotelIcon size={18} /> },
        { name: 'PKB', path: '/pkb', icon: <ImageIcon size={18} /> },
        { name: 'Dungeon Bot', path: '/dungeonbot', icon: <GamepadIcon size={18} /> },
    ];

    const userInitials = user?.displayName
        ? user.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
        : user?.username?.slice(0, 2).toUpperCase() || '?';

    return (
        <aside className="sidebar">
            {/* Brand */}
            <div className="flex items-center gap-3 px-2 mb-4">
                <div className="p-2 bg-white/5 rounded-xl border border-white/10">
                    <Bot size={24} className="text-white" />
                </div>
                <span className="text-xl font-bold tracking-tight text-white">
                    Outer Haven Bots
                </span>
            </div>

            {/* Search Stub */}
            <div className="relative mb-2">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted" />
                <input
                    className="w-full pl-10 pr-4 py-2 bg-panel-strong border border-stroke rounded-xl text-sm text-text placeholder-muted focus:outline-none focus:ring-1 focus:ring-accent/20 focus:border-accent/40 transition-all"
                    placeholder="Search..."
                    type="search"
                />
            </div>

            {/* Navigation */}
            <nav className="flex-1 flex flex-col gap-1">
                <div className="text-[10px] font-bold uppercase tracking-widest text-muted px-3 mb-2 opacity-50">
                    Bot Management
                </div>
                {navItems.map((item) => (
                    <Link
                        key={item.name}
                        to={item.path}
                        className={clsx(
                            "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group",
                            isActive(item.path)
                                ? "bg-white/10 text-white shadow-sm ring-1 ring-white/20"
                                : "text-muted hover:text-text hover:bg-white/5"
                        )}
                    >
                        <span className={clsx(
                            "transition-transform duration-200 group-hover:scale-110",
                            isActive(item.path) ? "text-accent" : "text-muted opacity-80"
                        )}>
                            {item.icon}
                        </span>
                        {item.name}
                    </Link>
                ))}

                <div className="mt-6 text-[10px] font-bold uppercase tracking-widest text-muted px-3 mb-2 opacity-50">
                    Administration
                </div>
                <Link
                    to="/permissions"
                    className={clsx(
                        "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group",
                        isActive('/permissions')
                            ? "bg-white/10 text-white shadow-sm ring-1 ring-white/20"
                            : "text-muted hover:text-text hover:bg-white/5"
                    )}
                >
                    <ShieldIcon size={18} className={clsx(
                        "transition-transform duration-200 group-hover:scale-110",
                        isActive('/permissions') ? "text-accent" : "text-muted opacity-80"
                    )} />
                    Permissions
                </Link>
            </nav>

            {/* Footer / User Profile */}
            <div className="mt-auto pt-4 border-t border-stroke space-y-4">
                <div className="flex items-center justify-between px-2">
                    <Button variant="ghost" size="icon" className="text-muted hover:text-text">
                        <BellIcon size={18} />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-muted hover:text-warning" onClick={() => logout()}>
                        <LogOutIcon size={18} />
                    </Button>
                </div>

                <div className="flex items-center gap-3 p-2 rounded-2xl bg-white/5 border border-white/5">
                    <Avatar
                        imageUrl={user?.avatarUrl || null}
                        initials={userInitials}
                        size="md"
                        variant="default"
                    />
                    <div className="flex flex-col min-w-0">
                        <span className="text-sm font-semibold text-text truncate">
                            {user?.displayName || user?.username}
                        </span>
                        <span className="text-xs text-muted truncate">
                            @{user?.username}
                        </span>
                    </div>
                </div>
            </div>
        </aside>
    );
};
